import React from "react"
import { useFormikContext } from "formik"
import { observer } from "mobx-react-lite"

import VoiceInputButton from "@components/ui/Button/VoiceInputButton"
import { useStore } from "@store/index"
import { useSpeechRecognizer } from "@pages/search/components/SearchWidget/useSpeechRecognizer"
import SearchInputField from "@pages/search/components/SearchWidget/SearchInputField"
import useMediaQuery from "@components/hooks/useMediaQuery"
import { AppMediaQueries } from "@framework/constants/app"
import Icon from "@components/ui/Icon/Icon"
import Button from "@components/ui/Button/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"

import { SearchSubmitButton } from "./SearchSubmitButton"
import { useSearchWidget } from "./components/Tabs/FactFinder/components/SearchWidget/SearchWidgetContext"

import styles from "./SearchInput.module.sass"

interface Props {
  disabled?: boolean
  loading?: boolean
}

export const SearchInput: React.FC<Props> = observer(
  ({ disabled, loading }) => {
    const { restrictionsStore: access } = useStore()
    const isMobile = useMediaQuery(AppMediaQueries.maxMobile)
    const formik = useFormikContext<any>()

    const searchWidget = useSearchWidget()

    const speechControl = useSpeechRecognizer({
      onChange: (value) => {
        formik.setFieldValue("query", value)
      },
    })

    const isListening = speechControl.listening
    const canSearch =
      !disabled && !isListening && formik.values.query.length > 0

    const hasSettings = access.allowWebSearch

    return (
      <>
        <SearchInputField
          disabled={isListening || disabled}
          placeholder="Ask a question"
          name="query"
          onFocus={() => searchWidget.setActive(true)}
          autoFocus
        />

        {access.isSearchVoiceRecognitionEnabled && (
          <VoiceInputButton
            disabled={disabled}
            size={isMobile ? "small" : "big"}
            listening={isListening}
            error={speechControl.error}
            onClick={() => speechControl.toggle()}
          />
        )}

        {hasSettings && (
          <Tooltip color="primary" closeOnTargetPress>
            <TooltipTrigger>
              <Button
                size="big"
                variant="outlined"
                noPadding
                onClick={() =>
                  searchWidget.showSettings(!searchWidget.isSettingsShown)
                }
              >
                <Icon
                  name={searchWidget.isSettingsShown ? "arrow-down" : "dots"}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {searchWidget.isSettingsShown ? "Hide" : "Show"} Tools
            </TooltipContent>
          </Tooltip>
        )}

        <SearchSubmitButton
          loading={loading}
          disabled={!canSearch || loading}
          className={styles.searchButton}
        />
      </>
    )
  }
)

export default SearchInput
